import { Cloud, Copy, Share2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { post, type Trip } from "../lib/api.ts";
import { Link, tripPath } from "../lib/router.tsx";
import { isNative, shareLink } from "../lib/session.ts";
import { Card, ICON, NestedCard, RadioCard, reason, useToast } from "../ui/index.ts";

type Step = "details" | "storage" | "invite";
const STEPS: { key: Step; label: string }[] = [
  { key: "details", label: "1 · Details" },
  { key: "storage", label: "2 · Storage" },
  { key: "invite", label: "3 · Invite" },
];

/** Midnight UTC of a yyyy-mm-dd date input, or undefined. */
const day = (v: string) => (v ? new Date(`${v}T00:00:00Z`).toISOString() : undefined);

/**
 * A. Create Trip: details, then storage (Cloud hosted is preselected and provisioned on create),
 * then invitations. Invitations come last because a link belongs to a trip that exists.
 */
export function CreateTripPage() {
  const [step, setStep] = useState<Step>("details");
  const [name, setName] = useState("");
  const [destination, setDestination] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [hosting, setHosting] = useState("cloud");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [trip, setTrip] = useState<Trip | null>(null);

  function next(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (start && end && end <= start) return setError("The trip must end after it starts.");
    setStep("storage");
  }

  async function create() {
    setError(null);
    setBusy(true);
    try {
      const body: Record<string, string> = { name: name.trim() };
      if (destination.trim()) body["destination"] = destination.trim();
      const s = day(start);
      const t = day(end);
      if (s) body["plannedStartAt"] = s;
      if (t) body["plannedEndAt"] = t;
      setTrip(await post<Trip>("/v1/trips", body));
      setStep("invite");
    } catch (e) {
      setError(reason(e as Error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack" style={{ maxWidth: "40rem" }}>
      <div>
        <h1>New trip</h1>
        <ol className="steps" aria-label="Steps">
          {STEPS.map((s) => (
            <li key={s.key} aria-current={s.key === step ? "step" : undefined}>
              {s.label}
            </li>
          ))}
        </ol>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {step === "details" && (
        <form className="card" onSubmit={next}>
          <label className="field">
            <span>Trip name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              required
            />
          </label>
          <label className="field">
            <span>
              Destination <span className="hint">(optional)</span>
            </span>
            <input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              maxLength={200}
            />
          </label>
          <div className="grid-2">
            <label className="field">
              <span>
                Starts <span className="hint">(planned)</span>
              </span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="field">
              <span>
                Ends <span className="hint">(planned)</span>
              </span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </div>
          <p className="muted small">
            Planned dates are for everyone’s information. Capture starts when you press Start trip,
            and only media taken between start and end is shared automatically.
          </p>
          <div className="row">
            <span className="spacer" />
            <button className="button primary" type="submit" disabled={!name.trim()}>
              Next
            </button>
          </div>
        </form>
      )}

      {step === "storage" && (
        <Card title="Where footage is kept">
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="sr">Storage</legend>
            <RadioCard
              name="hosting"
              value="cloud"
              checked={hosting === "cloud"}
              label="Cloud hosted"
              description="Ready now. A storage destination is set up for this trip automatically."
              onChange={setHosting}
            />
            <RadioCard
              name="hosting"
              value="self"
              checked={hosting === "self"}
              disabled
              label="Self hosted"
              description="Coming later: keep the originals on your own server."
              onChange={setHosting}
            />
          </fieldset>
          <NestedCard>
            <span className="row small muted">
              <Cloud {...ICON} aria-hidden="true" /> Originals are verified after upload before
              anything is shown as safe.
            </span>
          </NestedCard>
          <div className="row">
            <button className="button" type="button" onClick={() => setStep("details")}>
              Back
            </button>
            <span className="spacer" />
            <button className="button primary" type="button" onClick={create} disabled={busy}>
              {busy ? "Creating…" : "Create trip"}
            </button>
          </div>
        </Card>
      )}

      {step === "invite" && trip && <InviteStep trip={trip} />}
    </section>
  );
}

export function InviteStep({ trip, showGoToTrip = true }: { trip: Trip; showGoToTrip?: boolean }) {
  const toast = useToast();
  const [links, setLinks] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function make(kind: "traveller" | "editor") {
    setError(null);
    try {
      const r = await post<{ oneTimeUrl: string }>(`/v1/trips/${trip.id}/invitations`, {
        roles: kind === "traveller" ? ["CONTRIBUTOR"] : ["EDITOR", "REVIEWER"],
        expiresInHours: 72,
      });
      setLinks((l) => ({ ...l, [kind]: r.oneTimeUrl }));
    } catch (e) {
      setError(reason(e as Error));
    }
  }

  const canShare = isNative() || typeof navigator.share === "function";
  async function share(url: string) {
    if ((await shareLink(url, trip.name)) === "copied") toast("Link copied");
  }

  return (
    <Card title={`Invite people to ${trip.name}`}>
      <p className="muted">
        Each link works once and expires in 3 days. People who join choose what to share before
        anything leaves their phone.
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {(["traveller", "editor"] as const).map((kind) => (
        <NestedCard key={kind}>
          <div className="stack">
            <strong>{kind === "traveller" ? "Travellers" : "Editor"}</strong>
            {links[kind] ? (
              <div className="row">
                <label className="grow">
                  <span className="sr">{kind} invitation link</span>
                  <input readOnly value={links[kind]} onFocus={(e) => e.currentTarget.select()} />
                </label>
                <button className="button" type="button" onClick={() => share(links[kind]!)}>
                  {canShare ? <Share2 {...ICON} /> : <Copy {...ICON} />}
                  Share
                </button>
              </div>
            ) : (
              <div>
                <button className="button" type="button" onClick={() => make(kind)}>
                  {kind === "traveller" ? "Create traveller link" : "Create editor link"}
                </button>
              </div>
            )}
          </div>
        </NestedCard>
      ))}
      {showGoToTrip && (
        <div className="row">
          <span className="spacer" />
          <Link className="button primary" to={tripPath(trip.id)}>
            Go to the trip
          </Link>
        </div>
      )}
    </Card>
  );
}
